import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/utils";
import { Link } from "wouter";
import { Flag, Trophy, GraduationCap, Dumbbell } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface TimelineMilestone {
  id: string;
  title: string;
  description: string;
  date: string;
  type?: string;
  isHighlight?: boolean;
}

interface TimelineProps {
  milestones: TimelineMilestone[];
  loading?: boolean;
}

const TYPE_CONFIG: Record<string, { icon: LucideIcon; dot: string; badge: string }> = {
  achievement: {
    icon: Trophy,
    dot: "bg-[#E0A458]",
    badge: "bg-[#E0A458]/15 text-[#E0A458] border border-[#E0A458]/40",
  },
  recruiting: {
    icon: GraduationCap,
    dot: "bg-[#415A77]",
    badge: "bg-[#1B263B] text-slate-200 border border-[#415A77]",
  },
  training: {
    icon: Dumbbell,
    dot: "bg-[#415A77]",
    badge: "bg-[#1B263B] text-slate-200 border border-[#415A77]",
  },
};

const DEFAULT_CONFIG = {
  icon: Flag,
  dot: "bg-[#415A77]",
  badge: "bg-[#1B263B] text-slate-200 border border-[#415A77]",
};

const Timeline = ({ milestones, loading = false }: TimelineProps) => {
  const sortedMilestones = [...milestones].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  if (loading) {
    return <TimelineSkeleton />;
  }

  if (milestones.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-8 gap-3">
        <div className="rounded-full bg-[#1B263B] p-3">
          <Flag className="h-6 w-6 text-[#E0A458]/50" />
        </div>
        <p className="font-montserrat font-bold text-white">No milestones yet</p>
        <p className="text-sm text-slate-400">
          Add key moments in your athletic journey to build your timeline
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-[#415A77]/40"></div>

      <div className="space-y-8">
        {sortedMilestones.map((milestone) => {
          const config = (milestone.type && TYPE_CONFIG[milestone.type]) || DEFAULT_CONFIG;
          const Icon = config.icon;

          return (
            <div key={milestone.id} className="relative flex">
              <div
                className={`absolute left-4 md:left-6 -translate-x-1/2 w-3 h-3 ${config.dot} rounded-full border-2 border-[#0D1B2A] shadow-sm`}
              ></div>
              <div className="ml-8 md:ml-12">
                <h3 className="font-montserrat font-semibold text-white">{milestone.title}</h3>
                <p className="text-sm text-slate-400">{formatDate(milestone.date)}</p>

                {milestone.type && (
                  <Badge variant="outline" className={`mt-2 ${config.badge}`}>
                    <Icon className="h-3 w-3 mr-1" />
                    {milestone.type.charAt(0).toUpperCase() + milestone.type.slice(1)}
                  </Badge>
                )}

                <p className="mt-2 text-sm text-slate-300">{milestone.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TimelineSkeleton = () => (
  <div className="relative">
    <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-[#415A77]/40"></div>

    <div className="space-y-8">
      {[1, 2, 3].map((i) => (
        <div key={i} className="relative flex">
          <div className="absolute left-4 md:left-6 -translate-x-1/2 w-3 h-3 bg-[#415A77] rounded-full border-2 border-[#0D1B2A]"></div>
          <div className="ml-8 md:ml-12 w-full">
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-24 mb-3" />
            <Skeleton className="h-16 w-full" />
            <div className="flex gap-2 mt-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Timeline;
